﻿import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Empty,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { surveyPreviewAnalysisService } from '../../services/surveyPreviewAnalysis.service';
import { surveyAnalyticsService } from '../../services/surveyAnalytics.service';
import { surveyService } from '../../services/survey.service';
import type { ProfileField } from '../../services/surveyAnalysis.service';
import type {
  PreviewAnalysisStatus,
  PreviewAnalysisSummary,
} from '../../types/preview-analysis.types';
import { Survey } from '../../types/survey.types';
import AnalysisSummaryView from '../../components/analytics/AnalysisSummaryView';
import AnalyticsSegmentedView from '../../components/analytics/AnalyticsSegmentedView';
import AnalyticsExplorer from '../../components/analytics/explorer/AnalyticsExplorer';
import PreviewComparison from '../../components/analytics/explorer/PreviewComparison';
import type { VersionSummary } from '../../types/survey-analytics.types';

const { Title, Text } = Typography;

const SEGMENT_OPTIONS: Array<{ value: ProfileField; label: string }> = [
  { value: 'gender', label: 'GÃªnero' },
  { value: 'age_group', label: 'Faixa EtÃ¡ria' },
  { value: 'education', label: 'Escolaridade' },
  { value: 'economic_level', label: 'Renda' },
  { value: 'state', label: 'Estado' },
];

const STATUS_META: Record<PreviewAnalysisStatus, { color: string; label: string }> = {
  PENDING: { color: 'default', label: 'Aguardando' },
  IN_PROGRESS: { color: 'processing', label: 'Processando' },
  COMPLETED: { color: 'success', label: 'ConcluÃ­da' },
  FAILED: { color: 'error', label: 'Falhou' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR');
}

export function AdminSurveyPreviewAnalysisPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [criterion, setCriterion] = useState<ProfileField>('gender');
  const [triggering, setTriggering] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const {
    data: items = [],
    isLoading,
    error,
    refetch,
  } = useQuery<PreviewAnalysisSummary[]>({
    queryKey: ['survey-preview-analyses', surveyId],
    queryFn: () => surveyPreviewAnalysisService.listPreviewAnalyses(String(surveyId)),
    enabled: Boolean(surveyId),
    refetchInterval: (query) => {
      const rows = (query.state.data as PreviewAnalysisSummary[] | undefined) ?? [];
      return rows.some((r) => r.status === 'PENDING' || r.status === 'IN_PROGRESS')
        ? 4000
        : false;
    },
  });

  const { data: surveyDetails } = useQuery<Survey>({
    queryKey: ['survey', surveyId],
    queryFn: () => surveyService.getSurvey(String(surveyId)),
    enabled: Boolean(surveyId),
    staleTime: 60_000,
  });

  const hasInFlight = useMemo(
    () => items.some((r) => ['PENDING', 'IN_PROGRESS', 'CAPTURING', 'NORMALIZING', 'SUMMARIZING', 'ARCHIVING'].includes(r.status)),
    [items]
  );

  // Auto-seleciona a anÃ¡lise concluÃ­da mais recente quando nada estÃ¡ selecionado.
  useEffect(() => {
    if (selectedId && items.some((r) => r.id === selectedId)) return;
    const firstCompleted = items.find((r) => r.status === 'COMPLETED');
    setSelectedId(firstCompleted ? firstCompleted.id : null);
  }, [items, selectedId]);

  const { data: detail, isLoading: loadingDetail } = useQuery({
    queryKey: ['survey-preview-analysis', surveyId, selectedId],
    queryFn: () =>
      surveyPreviewAnalysisService.getPreviewAnalysis(String(surveyId), String(selectedId)),
    enabled: Boolean(surveyId && selectedId),
  });

  // O payload estatÃ­stico (pode passar de 6MB) Ã© baixado Ã  parte â€” direto do
  // bucket por URL prÃ©-assinada, sem trafegar pelo Lambda.
  const {
    data: payload,
    isLoading: loadingPayload,
    error: payloadError,
  } = useQuery({
    queryKey: ['survey-preview-analysis-payload', surveyId, selectedId],
    queryFn: () =>
      surveyPreviewAnalysisService.getPreviewAnalysisPayload(
        String(surveyId),
        String(selectedId)
      ),
    enabled: Boolean(surveyId && selectedId) && detail?.status === 'COMPLETED' && !detail?.versionId,
  });

  const { data: v2Questions } = useQuery({
    queryKey: ['survey-analytics-v2-questions', surveyId, detail?.versionId],
    queryFn: () =>
      surveyAnalyticsService.listQuestions(String(surveyId), String(detail?.versionId)),
    enabled: Boolean(surveyId && detail?.versionId && detail?.status === 'COMPLETED'),
  });

  const explorerVersions = useMemo<VersionSummary[]>(() => {
    return items
      .filter((item) => item.versionId && item.status === 'COMPLETED')
      .map((item) => ({
        versionId: item.versionId as VersionSummary['versionId'],
        surveyId: String(surveyId),
        kind: 'PREVIEW' as const,
        previewId: item.id,
        origin: 'NATIVE_SNAPSHOT' as const,
        status: item.status,
        requestedAt: item.createdAt,
        cutAt: null,
        generatedAt: item.generatedAt ?? null,
        participantCount: item.analyzedResponseCount ?? null,
        calculationVersion: 'statistics-v2',
        capabilities: { ranking: true, singleDimension: true, combinedFilters: true, pivot: true, export: true, compare: true },
      }));
  }, [items, surveyId]);

  const handleTrigger = async () => {
    try {
      setTriggering(true);
      const accepted = await surveyPreviewAnalysisService.triggerPreviewAnalysis(String(surveyId));
      messageApi.success(
        accepted.accepted
          ? 'AnÃ¡lise prÃ©via aceita e enfileirada para processamento.'
          : 'AnÃ¡lise prÃ©via executada.'
      );
      await refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        messageApi.warning('JÃ¡ existe uma anÃ¡lise prÃ©via em andamento para esta pesquisa.');
      } else if (status === 400) {
        messageApi.error(
          err?.response?.data?.message ||
            'A pesquisa precisa estar ativa e ter ao menos uma resposta.'
        );
      } else {
        messageApi.error(err?.message || 'Erro ao executar a anÃ¡lise prÃ©via.');
      }
    } finally {
      setTriggering(false);
    }
  };

  const handleRetry = async (id: string) => {
    try {
      setRetryingId(id);
      const accepted = await surveyPreviewAnalysisService.retryPreviewAnalysis(String(surveyId), id);
      messageApi.success(
        accepted.accepted
          ? 'Retry aceito e enfileirado para processamento.'
          : 'AnÃ¡lise prÃ©via reprocessada.'
      );
      await refetch();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.message || err?.message || 'Erro ao repetir a anÃ¡lise.');
    } finally {
      setRetryingId(null);
    }
  };

  const columns: ColumnsType<PreviewAnalysisSummary> = [
    {
      title: 'Gerada em',
      key: 'generatedAt',
      render: (_, row) => formatDateTime(row.generatedAt ?? row.createdAt),
    },
    {
      title: 'Executada por',
      key: 'triggeredBy',
      render: (_, row) => row.triggeredBy?.name ?? '-',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, row) => {
        const meta = STATUS_META[row.status];
        return (
          <Space size={4}>
            <Tag color={meta.color}>{meta.label}</Tag>
            {row.status === 'FAILED' && row.failureReason && (
              <Tooltip title={row.failureReason}>
                <Text type="danger" style={{ fontSize: 12 }}>
                  motivo
                </Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Respondentes vÃ¡lidos',
      key: 'analyzedResponseCount',
      render: (_, row) => row.analyzedResponseCount ?? '-',
    },
    {
      title: 'AÃ§Ãµes',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button
            type="link"
            style={{ padding: 0 }}
            disabled={row.status !== 'COMPLETED'}
            onClick={() => setSelectedId(row.id)}
          >
            Ver
          </Button>
          {row.status === 'FAILED' && (
            <Button
              type="link"
              icon={<ReloadOutlined />}
              style={{ padding: 0 }}
              loading={retryingId === row.id}
              onClick={() => handleRetry(row.id)}
            >
              Tentar novamente
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const questions = payload?.questions ?? [];

  return (
    <div className="space-y-4">
      {contextHolder}
      <Space style={{ marginBottom: 8 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/surveys')}>
          Voltar para pesquisas
        </Button>
      </Space>

      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
          <div>
            <Title level={3} style={{ marginBottom: 4 }}>
              AnÃ¡lises prÃ©vias
            </Title>
            <Text type="secondary">
              {surveyDetails?.name ?? `Pesquisa #${surveyId}`} â€” leitura antecipada dos
              resultados, visÃ­vel apenas para administradores. AnÃ¡lises prÃ©vias nunca sÃ£o
              publicadas.
            </Text>
          </div>
          <Tooltip
            title={
              hasInFlight
                ? 'Aguarde a anÃ¡lise prÃ©via em andamento concluir'
                : surveyDetails && surveyDetails.status !== 'ACTIVE'
                  ? 'A pesquisa precisa estar ativa (em coleta)'
                  : undefined
            }
          >
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={triggering}
              disabled={
                hasInFlight ||
                (Boolean(surveyDetails) && surveyDetails?.status !== 'ACTIVE') ||
                (surveyDetails?._count?.responses ?? 0) < 1
              }
              onClick={handleTrigger}
            >
              Executar nova anÃ¡lise prÃ©via
            </Button>
          </Tooltip>
        </Space>
      </Card>

      {error && (
        <Alert
          type="error"
          message="Erro ao carregar as anÃ¡lises prÃ©vias"
          action={
            <Button size="small" onClick={() => refetch()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      <Card title="Linha do tempo">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <Empty description="Nenhuma anÃ¡lise prÃ©via executada para esta pesquisa." />
        ) : (
          <Table
            rowKey="id"
            dataSource={items}
            columns={columns}
            pagination={false}
            size="small"
            rowClassName={(row) => (row.id === selectedId ? 'ant-table-row-selected' : '')}
          />
        )}
      </Card>

      {selectedId && (
        <Card
          title={`Resultado da an?lise pr?via de ${formatDateTime(
            detail?.generatedAt ?? detail?.createdAt ?? null
          )}`}
        >
          {loadingDetail || loadingPayload ? (
            <div className="flex justify-center py-8">
              <Spin />
            </div>
          ) : detail?.status === 'FAILED' ? (
            <Alert
              type="error"
              message="Esta an?lise pr?via falhou"
              description={detail.failureReason ?? 'Motivo n?o informado.'}
            />
          ) : payloadError ? (
            <Alert
              type="error"
              message="N?o foi poss?vel carregar o resultado desta an?lise pr?via"
              description={(payloadError as Error)?.message ?? 'Tente novamente em instantes.'}
            />
          ) : detail?.versionId && v2Questions ? (
            <div className="space-y-4">
              <Alert
                type="info"
                message="An?lise pr?via processada em modo leve"
                description="Explore perguntas e rankings paginados sem baixar o JSON completo da an?lise."
              />
              <AnalyticsExplorer
                surveyId={String(surveyId)}
                versions={explorerVersions.length ? explorerVersions : []}
                questions={v2Questions.items}
                initialVersionId={detail.versionId}
                initialQuestionId={v2Questions.items[0]?.questionId}
              />
              <PreviewComparison
                surveyId={String(surveyId)}
                versions={explorerVersions}
                questionId={v2Questions.items[0]?.questionId ?? '1'}
                optionKey="cand-1"
              />
            </div>
          ) : questions.length === 0 ? (
            <Text type="secondary">Nenhuma pergunta com dados estat?sticos dispon?vel.</Text>
          ) : (
            <div className="space-y-4">
              <Space>
                <Text strong>Segmentar por:</Text>
                <Select
                  value={criterion}
                  onChange={(v) => setCriterion(v)}
                  options={SEGMENT_OPTIONS}
                  style={{ width: 180 }}
                />
              </Space>
              <AnalysisSummaryView questions={questions} />
              <div>
                <Title level={5}>Por perfil</Title>
                <AnalyticsSegmentedView questions={questions} criterion={criterion} />
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

export default AdminSurveyPreviewAnalysisPage;

